import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DamageReport = ({ reports, onAddReport, onRemoveReport }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAddingReport, setIsAddingReport] = useState(false);
  const [newReport, setNewReport] = useState({
    location: '',
    severity: 'minor',
    description: ''
  });

  const severityOptions = [
    { value: 'minor', label: 'Minor', color: 'text-warning', bgColor: 'bg-warning/10' },
    { value: 'moderate', label: 'Moderate', color: 'text-orange-500', bgColor: 'bg-orange-500/10' },
    { value: 'major', label: 'Major', color: 'text-error', bgColor: 'bg-error/10' }
  ];

  const handleAddReport = () => {
    if (!newReport?.location || !newReport?.description) {
      alert('Please fill in all required fields');
      return;
    }
    onAddReport({
      ...newReport,
      timestamp: new Date()?.toISOString()
    });
    setNewReport({ location: '', severity: 'minor', description: '' });
    setIsAddingReport(false);
  };

  const getSeverityStyle = (severity) => {
    return severityOptions?.find(opt => opt?.value === severity) || severityOptions?.[0];
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-smooth"
      >
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center">
            <Icon name="AlertTriangle" size={20} className="text-warning" strokeWidth={2} />
          </div>
          <div className="text-left">
            <h3 className="font-heading text-lg font-semibold text-foreground">
              Damage Reports
            </h3>
            <p className="font-body text-sm text-muted-foreground">
              Document any damage discovered during use
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {reports?.length > 0 && (
            <span className="px-2 py-1 bg-warning/10 text-warning rounded-full text-xs font-semibold">
              {reports?.length}
            </span>
          )}
          <Icon
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            size={20}
            strokeWidth={2}
            className="text-muted-foreground"
          />
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 border-t border-border space-y-4">
          {/* Existing Reports */}
          {reports?.length > 0 && (
            <div className="space-y-3">
              {reports?.map(report => {
                const severityStyle = getSeverityStyle(report?.severity);
                return (
                  <div key={report?.id} className="bg-muted rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          severityStyle?.bgColor
                        } ${severityStyle?.color}`}>
                          {severityStyle?.label}
                        </span>
                        <span className="font-body text-sm font-medium text-foreground">
                          {report?.location}
                        </span>
                      </div>
                      <button
                        onClick={() => onRemoveReport(report?.id)}
                        className="p-1 hover:bg-background rounded transition-smooth"
                        aria-label="Remove report"
                      >
                        <Icon name="X" size={16} strokeWidth={2} className="text-muted-foreground" />
                      </button>
                    </div>
                    <p className="font-body text-sm text-muted-foreground">
                      {report?.description}
                    </p>
                    <p className="font-caption text-xs text-muted-foreground mt-2">
                      Reported at {new Date(report?.timestamp)?.toLocaleString()}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add New Report Form */}
          {isAddingReport ? (
            <div className="bg-muted rounded-lg p-4 space-y-4">
              <div>
                <label className="font-body text-sm font-medium text-foreground mb-2 block">
                  Damage Location *
                </label>
                <input
                  type="text"
                  value={newReport?.location}
                  onChange={(e) => setNewReport({ ...newReport, location: e?.target?.value })}
                  placeholder="e.g., Front left fender, Rear door, etc."
                  className="w-full px-4 py-2 rounded-md border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="font-body text-sm font-medium text-foreground mb-2 block">
                  Severity Level *
                </label>
                <div className="flex gap-2">
                  {severityOptions?.map(option => (
                    <button
                      key={option?.value}
                      onClick={() => setNewReport({ ...newReport, severity: option?.value })}
                      className={`flex-1 px-4 py-2 rounded-md font-body text-sm font-medium transition-smooth ${
                        newReport?.severity === option?.value
                          ? `${option?.bgColor} ${option?.color} border-2 border-current`
                          : 'bg-background text-muted-foreground border-2 border-border hover:border-primary'
                      }`}
                    >
                      {option?.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="font-body text-sm font-medium text-foreground mb-2 block">
                  Description *
                </label>
                <textarea
                  value={newReport?.description}
                  onChange={(e) => setNewReport({ ...newReport, description: e?.target?.value })}
                  placeholder="Describe the damage in detail..."
                  maxLength={500}
                  rows={3}
                  className="w-full px-4 py-3 rounded-md border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                />
                <p className="font-caption text-xs text-muted-foreground mt-1 text-right">
                  {newReport?.description?.length}/500 characters
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsAddingReport(false);
                    setNewReport({ location: '', severity: 'minor', description: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAddReport}
                  iconName="Plus"
                  className="flex-1"
                >
                  Add Report
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => setIsAddingReport(true)}
              iconName="Plus"
              className="w-full"
            >
              Report Damage
            </Button>
          )}

          {/* Info Box */}
          {reports?.length === 0 && !isAddingReport && (
            <div className="bg-primary/5 border border-primary/20 rounded-md p-4 flex items-start space-x-3">
              <Icon name="Info" size={16} className="text-primary mt-0.5" strokeWidth={2} />
              <div className="flex-1">
                <p className="font-body text-sm text-foreground">
                  No damage reports yet. If you discovered any damage during your rental period, 
                  please document it here to ensure transparency.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DamageReport;